import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  DvButtonComponent,
  DvDividerComponent,
  DvSegmentedButtonComponent,
  DvSegmentedButtonItem,
  DvToggleComponent,
} from '@devinco/ui/dds';
import { FootprintIconPath, RouteColorIcons, RouteColors } from '@features/map/constants/constants';
import { PoiTypeGroup } from '@features/map/constants/poi-types';
import { PoiVisibilityService } from '@features/map/services/poi-visibility.service';
import LayerColorUtils from '@features/map/utils/layer-color.utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

type LegendView = 'trails' | 'pointsOfInterest';

interface LegendPoiType {
  key: string;
  name: string;
  iconHtml: SafeHtml;
  isShown: boolean;
}

interface LegendPoiGroup {
  group: PoiTypeGroup;
  titleKey: string;
  types: LegendPoiType[];
  isAllShown: boolean;
}

const PoiGroupTitleKeys: Record<PoiTypeGroup, string> = {
  [PoiTypeGroup.Warnings]: 'Map.Legend.Warnings',
  [PoiTypeGroup.Information]: 'Map.Legend.Information',
};

const SafeIconColor = /^(#[0-9a-f]{3,8}|[a-z]+)$/i;

@Component({
  selector: 'app-legend-panel',
  imports: [
    DvButtonComponent,
    DvDividerComponent,
    DvSegmentedButtonComponent,
    DvToggleComponent,
    TranslatePipe,
  ],
  templateUrl: './legend-panel.component.html',
  styleUrl: './legend-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'closed.emit()' },
})
export class LegendPanelComponent {
  private translate = inject(TranslateService);
  private sanitizer = inject(DomSanitizer);
  private poiVisibilityService = inject(PoiVisibilityService);

  closed = output<void>();

  readonly FootprintIconPath = FootprintIconPath;
  readonly walkingMarkerBorderColor = RouteColors.Level7;
  readonly selectedView = signal<LegendView>('trails');
  readonly viewItems = toSignal(
    this.translate.stream(['Map.Legend.Trails', 'Map.Legend.PointsOfInterest']).pipe(
      map((texts): DvSegmentedButtonItem<LegendView>[] => [
        { value: 'trails', label: texts['Map.Legend.Trails'] },
        { value: 'pointsOfInterest', label: texts['Map.Legend.PointsOfInterest'] },
      ]),
    ),
    { initialValue: [] },
  );
  readonly lastGroomedLevels = [
    { textKey: 'Map.Legend.Level1', iconUrl: RouteColorIcons.Level1 },
    { textKey: 'Map.Legend.Level2', iconUrl: RouteColorIcons.Level2 },
    { textKey: 'Map.Legend.Level3', iconUrl: RouteColorIcons.Level3 },
    { textKey: 'Map.Legend.Level4', iconUrl: RouteColorIcons.Level4 },
    { textKey: 'Map.Legend.Level5', iconUrl: RouteColorIcons.Level5 },
    { textKey: 'Map.Legend.Level6', iconUrl: RouteColorIcons.Level6 },
    { textKey: 'Map.Legend.Level7', iconUrl: RouteColorIcons.Level7 },
  ];

  private readonly lang = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.getCurrentLang(),
  });

  readonly shownPoiTypesCount = this.poiVisibilityService.shownCount;
  readonly poiTypesCount = computed(() => this.poiVisibilityService.types().length);
  readonly isAllPoiTypesShown = computed(() => !this.poiVisibilityService.hasHiddenTypes());
  readonly poiGroups = computed<LegendPoiGroup[]>(() => {
    const collator = new Intl.Collator(this.lang() || undefined);
    const hiddenKeys = this.poiVisibilityService.hiddenKeys();
    const types = this.poiVisibilityService.types();

    return [PoiTypeGroup.Warnings, PoiTypeGroup.Information]
      .map((group) => {
        const groupTypes = types
          .filter((type) => type.group === group)
          .map((type) => ({
            key: type.key,
            name: this.translate.instant(type.key),
            iconHtml: this.sanitizer.bypassSecurityTrustHtml(
              LayerColorUtils.getIconWithFill(
                type.iconKey,
                SafeIconColor.test(type.iconColor) ? type.iconColor : 'red',
              ),
            ),
            isShown: !hiddenKeys.has(type.key),
          }))
          .sort((a, b) => collator.compare(a.name, b.name));

        return {
          group,
          titleKey: PoiGroupTitleKeys[group],
          types: groupTypes,
          isAllShown: groupTypes.every((type) => type.isShown),
        };
      })
      .filter((group) => group.types.length);
  });

  setAllPoiTypesShown(isShown: boolean): void {
    const keys = this.poiVisibilityService.types().map((type) => type.key);
    this.poiVisibilityService.setTypesShown(keys, isShown);
  }

  setPoiGroupShown(group: LegendPoiGroup, isShown: boolean): void {
    const keys = group.types.map((type) => type.key);
    this.poiVisibilityService.setTypesShown(keys, isShown);
  }

  setPoiTypeShown(key: string, isShown: boolean): void {
    this.poiVisibilityService.setTypesShown([key], isShown);
  }
}
